import {usePaidDeliveryCampaignSearchQuery} from "@/api/paidDeliveryApi";
import InputField from "@/components/Common/forms/InputField";
import Loader from "@/components/Common/kpi/Loader";
import {createFileRoute, Link} from "@tanstack/react-router";
import {ChevronRight, Search} from "lucide-react";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useDebounce, useTitle} from "react-use";

export const Route = createFileRoute("/_authenticated/_admin/admins/boost-estimate/")({
  component: BoostEstimateSearchPage,
});

function BoostEstimateSearchPage() {
  const {t} = useTranslation("boost_estimate");

  useTitle(t("title"));

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useDebounce(() => setDebouncedSearch(search), 400, [search]);
  const {data: campaigns, isLoading} = usePaidDeliveryCampaignSearchQuery(debouncedSearch);

  return (
    <div className="max-w-wide container mx-auto mt-6 mb-20 flex min-w-0 flex-col gap-6 px-4 sm:mt-8 sm:px-8">
      <div className="flex flex-col gap-1">
        <h1 className="bg-linear-(--primary-dark-gradient) bg-clip-text text-2xl font-bold text-transparent">
          {t("title")}
        </h1>
        <span className="text-neutral-grey-dark max-w-2xl text-sm">{t("search_subtitle")}</span>
      </div>
      <InputField
        autoFocus
        containerClassName="sm:max-w-96"
        className="border-neutral-grey-light h-10 rounded-lg pl-10 text-sm font-semibold placeholder:font-normal"
        icon={
          <Search className="text-neutral-grey-dark pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        }
        placeholder={t("search_placeholder")}
        value={search}
        onChange={setSearch}
      />
      {isLoading ? (
        <Loader styles={{containerClassName: "my-4", loaderClassName: "min-h-10"}} />
      ) : !campaigns?.length ? (
        <p className="text-neutral-grey-dark text-sm">{t("no_campaigns_found")}</p>
      ) : (
        <ul className="border-neutral-white-dark divide-neutral-white-dark flex flex-col divide-y rounded-lg border bg-white">
          {campaigns.map(campaign => (
            <li key={campaign.id}>
              <Link
                to="/admins/boost-estimate/$campaignId"
                params={{campaignId: String(campaign.id)}}
                className="hover:bg-neutral-white flex items-center gap-4 px-4 py-3 no-underline">
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-neutral-black truncate text-sm font-semibold">
                    {campaign.title || `#${campaign.id}`}
                  </span>
                  <span className="text-neutral-grey-dark truncate text-xs">
                    {[campaign.company_name, `#${campaign.id}`].filter(Boolean).join(" · ")}
                  </span>
                </div>
                <span className="text-neutral-grey-dark shrink-0 text-xs whitespace-nowrap">
                  {[
                    campaign.instagram_count && t("media_count_instagram", {count: campaign.instagram_count}),
                    campaign.tiktok_count && t("media_count_tiktok", {count: campaign.tiktok_count}),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                <ChevronRight aria-hidden="true" className="text-neutral-grey-dark size-4 shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
